import { useState, useEffect } from "react";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { getFaqCategories, getFaqItems } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import {
  Search, ChevronDown, HelpCircle, CreditCard, Smartphone, Wifi,
  Shield, Settings, Users, MapPin, FileText, Phone, Zap, Globe,
  Package, Headphones, BarChart3, Lock, Star, Truck, MessageCircle,
} from "lucide-react";

const ICON_MAP = {
  HelpCircle, CreditCard, Smartphone, Wifi, Shield, Settings, Users,
  MapPin, FileText, Phone, Zap, Globe, Package, Headphones, BarChart3,
  Lock, Star, Truck, MessageCircle,
};

function FaqAccordion({ question, answer, isOpen, onToggle, testId }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testId}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <span className="font-body font-medium text-navy pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-5 pb-5 text-slate-600 font-body text-sm leading-relaxed border-t border-slate-100 pt-4"
              dangerouslySetInnerHTML={{ __html: answer.replace(/\n/g, "<br/>") }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqPage() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [cats, faqs] = await Promise.all([
          getFaqCategories(true),
          getFaqItems(null, true),
        ]);
        setCategories(cats);
        setItems(faqs);
      } catch (e) {
        console.error("Failed to load FAQs", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = items.filter((item) => {
    const matchesCat = selectedCategory === "all" || item.category_id === selectedCategory;
    const matchesSearch = !search || item.question.toLowerCase().includes(search.toLowerCase()) || item.answer.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const getCategoryName = (catId) => categories.find((c) => c.id === catId)?.name || "General";

  return (
    <PublicLayout>
      <SEOHead
        title="FAQs | Prime Media Wireless"
        description="Find answers to frequently asked questions about Total Wireless dealerships, store operations, plans, services, and more at Prime Media Wireless."
        pageSlug="faqs"
      />

      {/* Hero */}
      <section className="bg-navy py-16 lg:py-20" data-testid="faq-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            FAQs
          </h1>
          <p className="text-slate-300 font-body text-base lg:text-lg mb-8">
            Common topics and questions
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search FAQs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-full text-base"
              data-testid="faq-search"
            />
          </div>
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-white mt-8 mb-3" data-testid="faq-hero-title">
            Who is Prime Media Wireless, and What do we provide?
          </h2>
          <p className="text-slate-300 font-body text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" data-testid="faq-hero-description">
            Prime Media Wireless is a premier total wireless master agent, offering no-contract wireless services with unlimited plans for calls, messages, and data. We help individuals and businesses set up and run wireless retail locations across the nation.
          </p>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-10 bg-slate-50 border-b border-slate-200" data-testid="faq-categories">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all ${
                  selectedCategory === "all"
                    ? "bg-teal text-white shadow-md"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-teal hover:text-teal"
                }`}
                data-testid="faq-cat-all"
              >
                All
              </button>
              {categories.map((cat) => {
                const Icon = ICON_MAP[cat.icon] || HelpCircle;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all ${
                      selectedCategory === cat.id
                        ? "bg-teal text-white shadow-md"
                        : "bg-white text-slate-600 border border-slate-200 hover:border-teal hover:text-teal"
                    }`}
                    data-testid={`faq-cat-${cat.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Items */}
      <section className="py-12 lg:py-16 bg-white" data-testid="faq-items">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="faq-empty">
              <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-navy text-xl mb-2">
                {search ? "No results found" : "No FAQs yet"}
              </h3>
              <p className="text-slate-500 font-body">
                {search ? "Try a different search term or browse all categories." : "Check back soon for frequently asked questions."}
              </p>
            </div>
          ) : (
            <>
              {selectedCategory === "all" && !search ? (
                // Group by category
                categories.map((cat) => {
                  const catItems = filtered.filter((i) => i.category_id === cat.id);
                  if (catItems.length === 0) return null;
                  const Icon = ICON_MAP[cat.icon] || HelpCircle;
                  return (
                    <div key={cat.id} className="mb-10" data-testid={`faq-group-${cat.id}`}>
                      <h2 className="font-heading font-bold text-xl text-navy mb-4 flex items-center gap-2">
                        <Icon className="w-5 h-5 text-teal" />
                        {cat.name}
                      </h2>
                      <div className="space-y-3">
                        {catItems.map((item) => (
                          <FaqAccordion
                            key={item.id}
                            question={item.question}
                            answer={item.answer}
                            isOpen={openId === item.id}
                            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                            testId={`faq-item-${item.id}`}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                // Flat list for filtered view
                <div className="space-y-3">
                  {filtered.map((item) => (
                    <FaqAccordion
                      key={item.id}
                      question={item.question}
                      answer={item.answer}
                      isOpen={openId === item.id}
                      onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                      testId={`faq-item-${item.id}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-slate-50 border-t border-slate-200" data-testid="faq-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-2xl text-navy mb-3">Can't find what you need?</h2>
          <p className="text-slate-600 font-body mb-6">Our team is here to help. Reach out and we'll get back to you.</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors"
            data-testid="faq-contact-cta"
          >
            <MessageCircle className="w-4 h-4" /> Contact Us
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
